import { z } from "zod";

/**
 * Source discriminator for ingredient references. Mirrors `App\Enum\IngredientReferenceSource`
 * on the backend. The `sure` source comes from the legacy sure-api catalog; the `foodpilot`
 * source is owned by the platform and stored in Postgres.
 */
export type IngredientReferenceSource = "sure" | "foodpilot";

export const INGREDIENT_REFERENCE_INTENDED_USES = ["human_food", "animal_feed"] as const;

export type IngredientReferenceIntendedUse = (typeof INGREDIENT_REFERENCE_INTENDED_USES)[number];

export const IngredientReferenceSourceSchema: z.ZodType<IngredientReferenceSource> = z.union([
    z.literal("sure"),
    z.literal("foodpilot"),
]);

export type IngredientReferenceUnifiedScore = {
    scoreId: number;
    value: number | null;
};

export const IngredientReferenceUnifiedScoreSchema: z.ZodType<IngredientReferenceUnifiedScore> = z.object({
    scoreId: z.number(),
    value: z.number().nullable(),
});

/**
 * Normalized ingredient reference shape returned by `/api/ingredient-references-unified`.
 * `compositeId` is the IRI identifier (`{source}-{numericId}`) used by the picker and the
 * batch lookup endpoint; `id` is the numeric per-source id, kept for legacy persistence
 * (`Ingredient.ingredientReferenceId` + `Ingredient.referenceSource`).
 *
 * `ssqId` is sure-only (platform references never have an SSQ); explicit `null` carries that.
 */
export type IngredientReferenceUnified = {
    "@id"?: string;
    compositeId: string;
    source: IngredientReferenceSource;
    id: number;
    name: string;
    code?: string | null;
    description?: string | null;
    cpcCode?: string | null;
    cpcLabel?: string | null;
    defaultOrigin?: string | null;
    organic?: boolean | null;
    fresh?: boolean | null;
    animal?: boolean | null;
    nonEdiblePartRatio?: number | null;
    rawToCookedRatio?: number | null;
    ssqId?: number | null;
    scores: IngredientReferenceUnifiedScore[];
};

export const IngredientReferenceUnifiedSchema: z.ZodType<IngredientReferenceUnified> = z.object({
    "@id": z.string().optional(),
    compositeId: z.string(),
    source: IngredientReferenceSourceSchema,
    id: z.number(),
    name: z.string(),
    code: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    cpcCode: z.string().nullable().optional(),
    cpcLabel: z.string().nullable().optional(),
    defaultOrigin: z.string().nullable().optional(),
    organic: z.boolean().nullable().optional(),
    fresh: z.boolean().nullable().optional(),
    animal: z.boolean().nullable().optional(),
    nonEdiblePartRatio: z.number().nullable().optional(),
    rawToCookedRatio: z.number().nullable().optional(),
    ssqId: z.number().nullable().optional(),
    scores: IngredientReferenceUnifiedScoreSchema.array(),
});

export const buildCompositeId = (source: IngredientReferenceSource, id: number): string => `${source}-${id}`;

/**
 * HTTP header set by the backend when the sure-api round-trip fails and the unified endpoint
 * falls back to platform-only data. Consumers can render a degraded banner from this signal.
 */
export const SURE_API_DEGRADED_HEADER = "x-sure-api-degraded" as const;
